import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import {
  Bid,
  BidsCollection,
  Datastore,
  DatastoreDocument,
  ProjectViewProject,
  ProjectViewProjectsCollection,
  ProjectViewUser,
  ProjectViewUsersCollection,
  UsersCollection,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { toNumber } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

interface Payload {
  bid: Bid;
  user: ProjectViewUser;
  project: ProjectViewProject;
}

@Component({
  template: `
    <fl-bit class="Container">
      <ng-container *ngIf="payload$ | async as payload; else Loading">
        <app-hourly-hourly-award-modal
          *ngIf="payingHourly"
          flTrackingSection="HourlyHourlyAwardModal"
          [bid]="payload.bid"
          [project]="payload.project"
          (switchAwardType)="switchAwardType($event)"
          (close)="close($event)"
        ></app-hourly-hourly-award-modal>
        <app-hourly-fixed-award-modal
          *ngIf="!payingHourly"
          flTrackingSection="HourlyFixedAwardModal"
          [bid]="payload.bid"
          [user]="payload.user"
          [showNormalAwardModal]="showNormalAwardModal$ | async"
          [project]="payload.project"
          (switchAwardType)="switchAwardType($event)"
          (close)="close($event)"
        ></app-hourly-fixed-award-modal>
      </ng-container>
      <ng-template #Loading>
        <fl-spinner
          flTrackingLabel="HourlyAwardModalInitialisationSpinner"
          [overlay]="true"
        ></fl-spinner>
      </ng-template>
    </fl-bit>
  `,
  styleUrls: ['./hourly-award-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HourlyAwardModalComponent implements OnInit {
  @Input() bidId: number;

  payingHourly: boolean;

  bidDoc: DatastoreDocument<BidsCollection>;
  userDoc: DatastoreDocument<ProjectViewUsersCollection>;
  projectDoc: DatastoreDocument<ProjectViewProjectsCollection>;
  payload$: Rx.Observable<Payload>;
  showNormalAwardModal$: Rx.Observable<boolean>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private modalRef: ModalRef<HourlyAwardModalComponent>,
  ) {}

  ngOnInit() {
    this.payingHourly = true;

    this.bidDoc = this.datastore.document<BidsCollection>('bids', this.bidId);
    const bidderId$ = this.bidDoc.valueChanges().pipe(map(b => b.bidderId));

    this.userDoc = this.datastore.document<ProjectViewUsersCollection>(
      'projectViewUsers',
      bidderId$,
    );

    this.projectDoc = this.datastore.document<ProjectViewProjectsCollection>(
      'projectViewProjects',
      this.bidDoc.valueChanges().pipe(map(b => b.projectId)),
    );

    this.payload$ = Rx.combineLatest([
      this.bidDoc.valueChanges(),
      this.userDoc.valueChanges(),
      this.projectDoc.valueChanges(),
    ]).pipe(
      map(([bid, user, project]) => ({
        bid,
        user,
        project,
      })),
    );

    const escrowRequired$ = this.datastore
      .objects<UsersCollection>(
        'users',
        Rx.combineLatest([this.auth.getUserId(), bidderId$]).pipe(
          map(([authUid, bidderId]) => [toNumber(authUid), bidderId]),
        ),
      )
      .pipe(map(users => users.some(u => u.escrowComInteractionRequired)));

    this.showNormalAwardModal$ = escrowRequired$.pipe(
      map(escrowRequired => !escrowRequired),
    );
  }

  close(event: boolean) {
    this.modalRef.close(event);
  }

  switchAwardType(awardType: string) {
    this.payingHourly = awardType === ProjectTypeApi.HOURLY;
  }
}
